




































































































































































import {
  Vue,
  Component,
  Prop,
  Watch,
  Inject
} from "@/shared/vuepack";
import { Validator } from "vee-validate";
import {
  EmployeeSearchResultDetail,
  InvestmentAccountType
} from "@/employer/types";
import Button from "@/shared/components/ui/Button.vue";
import {
  Permission,
  SortOrder,
  EmployeeSearchSortOption,
  employeeSearchSortOptionMap,
  UserPermission
} from "@/shared/types";
import Pagination from "@/shared/components/ui/Pagination.vue";
import { PartnerEmployeeSearch } from "@/employer/route-names";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useUserStore } from "@/shared/store/user";
import { useNotifierStore } from "@/shared/store/notifier";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useEmployeeSearchStore } from "@/shared/store/employeeSearch";
import { useImpersonatorStore } from "@/shared/store/impersonator";
import { useNavigationStore } from "@/shared/store/navigation";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";
import { useSearchCapabilityStore } from "@/shared/store/searchCapability";

const initialSearchTerms = {
  firstName: "",
  lastName: "",
  email: "",
  ssn4: "",
  login: "",
  accountNumber: "",
  investmentAccountNumber: "",
  employerEmployeeId: "",
  externalEmployerId: "",
  sortOrder: SortOrder.None,
  sortOption: EmployeeSearchSortOption.Relevance
};

@Component({
  components: { Button, Pagination, GenericLoadingSpinner }
})
export default class EmployeeSearch extends Vue {
  @Inject()
  $validator: Validator;
  employeeSearchSortOptionMap = employeeSearchSortOptionMap;

  userStore = useUserStore();
  notifierStore = useNotifierStore();
  featureFlagStore = useFeatureFlagStore();
  employeeSearchStore = useEmployeeSearchStore();
  impersonatorStore = useImpersonatorStore();
  navigationStore = useNavigationStore();
  rolePermissionsStore = useRolePermissionsStore();
  searchCapabilityStore = useSearchCapabilityStore();

  @Prop() partnerId: number;

  @Prop() hideImpersonation: boolean;

  @Prop({ required: false, default: null })
  routeToEmployeeCustomerService: (username: string) => Location;

  pageNumber = 1;
  pageSize = 10;

  get pageCount () {
    return Math.ceil(this.searchResultNumberFound / this.pageSize);
  }

  get searchResultNumberFound () {
    if (this.employeeSearchStore.searchResult === null) return 0;
    return this.employeeSearchStore.searchResult.numberFound;
  }

  get queryTime () {
    if (this.employeeSearchStore.searchResult === null) return 0;
    return this.employeeSearchStore.searchResult.elapsedMilliseconds;
  }

  get maxScore () {
    if (this.employeeSearchStore.searchResult === null) return 0;
    return this.employeeSearchStore.searchResult.maxScore;
  }

  get isFormValid () {
    return !this.$validator.errors.any();
  }

  sortDescending = false;
  sortOption = EmployeeSearchSortOption.Relevance;
  maxAccountNumberLength = 20;

  searchTermFormModel = { ...initialSearchTerms };

  isSubmitting = false;
  isImpersonationSubmitting = false;

  onSearch (resetPage = false) {
    if (this.isSubmitting) return;
    if (resetPage) {
      this.pageNumber = 1;
    }
    this.$validator
      .validate()
      .then(result => {
        if (!result) return;
        this.isSubmitting = true;
        return this.employeeSearchStore.getEmployeeSearchResults({
          partnerId: this.partnerId,
          searchTerms: this.searchTermFormModel,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize
        })
          .catch(err => {
            this.onSearchError(err);
          });
      })
      .finally(() => this.isSubmitting = false);
  }

  onSearchError (err) {
    this.notifierStore.setDanger(err.message);
    this.isSubmitting = false;
  }

  @Watch("partnerId", { immediate: true })
  changePartner () {
    if (this.partnerId !== this.employeeSearchStore.selectedPartnerId) {
      this.employeeSearchStore.clearEmployeeSearchResult();
    }
  }

  clearSearch (): void {
    if (!this.isSubmitting) {
      this.isSubmitting = true;
      this.searchTermFormModel = { ...initialSearchTerms };
      this.sortDescending = false;
      this.sortOption = this.defaultSortOption;
      this.isSubmitting = false;
    }
    this.employeeSearchStore.clearEmployeeSearchResult();
  }

  mounted () {
    this.searchCapabilityStore.loadSearchCabability();
    this.searchTermFormModel = { ...this.employeeSearchStore.searchTerm };
    this.pageNumber = this.employeeSearchStore.selectedPageNumber;
    this.sortOption = this.defaultSortOption;
    this.sortDescending = false;
  }

  get defaultSortOption () {
    return this.searchCapabilityStore.isUserSearchEnabled
      ? EmployeeSearchSortOption.Relevance
      : EmployeeSearchSortOption.Name;
  }

  get isSearchDisabled () {
    return this.isSubmitting === true || !this.isFormValid;
  }

  get isClearDisabled () {
    return this.isSubmitting === true;
  }

  showLink (e: EmployeeSearchResultDetail) {
    return !!e.username && this.routeToEmployeeCustomerService
      && (this.rolePermissionsStore.userHasPermission(Permission.HsaAccountInfoRead)
        || this.rolePermissionsStore.userHasPermission(Permission.PartnerManageUserLogin)
        || this.rolePermissionsStore.userHasPermission(Permission.PartnerFindEmployeeRead)
        || this.rolePermissionsStore.userHasPermission(Permission.PartnerManageIdp));
  }

  impersonate (cognitoUsername: string) {
    const newTab = window.open();
    this.isImpersonationSubmitting = true;
    this.impersonatorStore.redirectToImpersonation(
      { targetCognitoUsername: cognitoUsername, newTab, partnerId: this.partnerId })
      .finally(() => {
        this.isImpersonationSubmitting = false;
      });
  }

  get showImpersonation () {
    return !this.hideImpersonation && this.rolePermissionsStore.userHasPermission(Permission.PartnerImpersonateEmployee);
  }

  get hasInvestmentViewPermission (): boolean {
    return this.rolePermissionsStore.doesUserHavePermission(
      UserPermission.PartnerPortalInvestmentView
    );
  }

  onPageChanged (pageNumber: number): void {
    this.pageNumber = pageNumber;
    this.onSearch();
  }

  onEmployeeRouting (employeeUsername) {
    this.navigationStore.setLastPartnerPortalTab(PartnerEmployeeSearch);
    return this.routeToEmployeeCustomerService(employeeUsername);
  }

  accountAndTypeFormat (item: EmployeeSearchResultDetail, index: number): string {
    let text = "";
    if (item.investmentAccountNumbers && item.investmentAccountNumbers.length > index) {
      text += `Investment: ${item.investmentAccountNumbers[index]}`;
    }

    if (item.investmentAccountTypes && item.investmentAccountTypes.length > index) {
      if (text.length > 0) {
        text += " - ";
      }
      text += "Type: ";
      switch (item.investmentAccountTypes[index]) {
        case InvestmentAccountType.RiaManaged:
            text += "Managed";
            break;
        case InvestmentAccountType.Advisory:
            text += "Select";
            break;
        case InvestmentAccountType.OpenBrokerage:
            text += "Choice";
            break;
        default:
            text += "Unknown";
            break;
      }
    }
    return text;
  }

  tableFields = [
    {
      key: "employee",
      label: "Name",
      thClass: "text-left",
      tdClass: "pl-2",
      sortable: true,
      sortKey: EmployeeSearchSortOption.Name
    },
    {
      key: "enrolled",
      label: "Status",
      thClass: "text-left",
      tdClass: "text-left",
      sortable: true,
      sortKey: EmployeeSearchSortOption.Status
    },
    {
      key: "employer",
      label: "ER Group Name",
      thClass: "text-left",
      tdClass: "text-left",
      sortable: true,
      sortKey: EmployeeSearchSortOption.Employer
    },
    {
      key: "partnerName",
      label: "Partner",
      thClass: "text-left",
      tdClass: "text-left",
      sortable: true,
      sortKey: EmployeeSearchSortOption.Partner
    },
    {
      key: "emailAddress",
      label: "E-Mail Address",
      thClass: "text-left",
      tdClass: "text-left",
      sortable: true,
      sortKey: EmployeeSearchSortOption.Email
    },
    {
      key: "lastFourSsn",
      label: "SSN4",
      thClass: "text-left",
      tdClass: "text-left",
      sortable: true,
      sortKey: EmployeeSearchSortOption.Ssn
    },
    {
      key: "hsaBankAccountNumber",
      label: "Account Number",
      thClass: "text-left",
      tdClass: "text-left",
      sortable: true,
      sortKey: EmployeeSearchSortOption.AccountNumber
    },
    {
      key: "employeeId",
      label: "Employee ID",
      thClass: "text-left",
      tdClass: "text-left",
      sortable: true,
      sortKey: EmployeeSearchSortOption.EmployeeId
    },
    {
      key: "impersonate",
      label: "",
      thClass: "text-left",
      tdClass: "text-center"
    }
  ];

  sortingChanged (ctx) {
    this.searchTermFormModel.sortOption = ctx.sortBy;
    this.sortOption = ctx.sortBy;
    this.sortDescending = ctx.sortDesc;
    this.searchTermFormModel.sortOrder = ctx.sortDesc
      ? SortOrder.Descending
      : SortOrder.Ascending;
    this.pageNumber = 1;
    this.onSearch();
  }
  get canSearchByEmployeeId (): boolean {
    return this.featureFlagStore.flags.bend26732EnableSearchByEmployeeId;
  }
}
